import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlayIcon, 
  PauseIcon, 
  SpeakerWaveIcon, 
  SpeakerXMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  AcademicCapIcon,
  SparklesIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  StarIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  VideoCameraIcon,
  ArrowLeftIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { CourseLesson, CourseQuiz } from '../types';
import InteractiveQuiz from './InteractiveQuiz';
import YouTubePlayer from './YouTubePlayer';
import GoogleDrivePlayer from './GoogleDrivePlayer';
import ZoomPlayer from './ZoomPlayer';
import ArticleContent from './ArticleContent';
import AssignmentContent from './AssignmentContent';
import { analyzeVideoUrl, getVideoPlayerType } from '../utils/videoUtils';
import { getCleanText } from '../utils/htmlRenderer';
import { AIService } from '../utils/aiService';

interface LessonContentProps {
  lesson: CourseLesson;
  courseId: string;
  courseName?: string;
  courseDescription?: string;
  onComplete: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const LessonContent: React.FC<LessonContentProps> = ({ 
  lesson, 
  courseId,
  courseName,
  courseDescription,
  onComplete, 
  onNext, 
  onPrevious 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<{ [questionIndex: number]: number }>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const lastSavedAtRef = React.useRef<number>(0);
  const lastSavedProgressRef = React.useRef<number>(0);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // AI Assistant state
  const [showAI, setShowAI] = useState(false);
  const [aiMode, setAiMode] = useState<'summary' | 'quiz' | 'flashcards' | 'ask'>('summary');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string>('');
  const [aiResult, setAiResult] = useState<any>(null);
  const [aiQuestion, setAiQuestion] = useState<string>('');

  // AI Context selection toggles
  const hasArticle = Boolean(lesson.article_content);
  const hasVideo = Boolean(lesson.video_url);
  const hasQuiz = Boolean(lesson.quiz && lesson.quiz.length > 0);
  const hasText = Boolean(lesson.description);
  const [useCtxArticle, setUseCtxArticle] = useState<boolean>(hasArticle);
  const [useCtxVideo, setUseCtxVideo] = useState<boolean>(hasVideo);
  const [useCtxQuiz, setUseCtxQuiz] = useState<boolean>(hasQuiz);
  const [useCtxText, setUseCtxText] = useState<boolean>(hasText);

  // Handle video content
  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };
  const handleVideoTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const newCurrentTime = e.currentTarget.currentTime;
    const newDuration = e.currentTarget.duration;
    
    setCurrentTime(newCurrentTime);
    setDuration(newDuration);
    
    // Throttle progress writes: every 30s or on >=5% progress change
    if (newDuration > 0) {
      const newProgress = Math.round((newCurrentTime / newDuration) * 100);
      const now = Date.now();
      const elapsed = now - (lastSavedAtRef.current || 0);
      const progressDelta = Math.abs(newProgress - (lastSavedProgressRef.current || 0));
      const shouldSave = (elapsed >= 30000) || (progressDelta >= 5);
      if (shouldSave && newProgress !== progress) {
        saveProgress(newProgress, {
          timeSpent: newCurrentTime,
          quizScore: quizScore,
          quizSubmitted: quizSubmitted,
          quizAnswers: quizAnswers
        });
        lastSavedAtRef.current = now;
        lastSavedProgressRef.current = newProgress;
      }
    }
  };
  
  const handleVideoEnded = () => {
    setIsPlaying(false);
    saveProgress(100, {
      timeSpent: duration,
      quizScore: quizScore,
      quizSubmitted: quizSubmitted,
      quizAnswers: quizAnswers
    }); // Mark as 100% complete
    onComplete();
  };

  // Save on unmount as a final checkpoint
  useEffect(() => {
    return () => {
      if (courseId && lesson.id) {
        const finalProgress = progress;
        saveProgress(finalProgress, {
          timeSpent: currentTime,
          quizScore: quizScore,
          quizSubmitted: quizSubmitted,
          quizAnswers: quizAnswers
        });
      }
    };
  }, [courseId, lesson.id, progress, currentTime, quizScore, quizSubmitted, quizAnswers]);

  // Handle quiz content
  const handleQuizAnswer = (questionIndex: number, answerIndex: number) => {
    setQuizAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionIndex]: answerIndex
      };
      return newAnswers;
    });
  };

  const handleQuizSubmit = () => {
    if (!lesson.quiz || lesson.quiz.length === 0) return;
    
    let correctAnswers = 0;
    lesson.quiz.forEach((question, index) => {
      const userAnswer = quizAnswers[index];
      const correctAnswer = question.correct_ans_index;
      const isCorrect = userAnswer === correctAnswer;
      
      if (isCorrect) {
        correctAnswers++;
      }
    });
    
    const score = (correctAnswers / lesson.quiz.length) * 100;
    const newProgress = score >= 70 ? 100 : Math.round(score);
    
    // Update state first
    setQuizScore(score);
    setQuizSubmitted(true);
    
    // Save quiz progress with updated data
    const quizData = {
      quizScore: score,
      quizSubmitted: true,
      quizAnswers: { ...quizAnswers }
    };
    
    saveProgress(newProgress, quizData);
    
    if (score >= 70) { // 70% passing score
      onComplete();
    }
  };

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Save progress to Firestore
  const saveProgress = async (newProgress: number, additionalData: any = {}) => {
    if (!courseId || !lesson.id) return;
    
    setIsSaving(true);
    try {
      const { doc, setDoc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      const { getAuth } = await import('firebase/auth');
      
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        console.error('❌ No user ID found');
        return;
      }

      // Save to user's course progress
      const progressRef = doc(db, 'users', userId, 'CourseProgress', courseId);
      const courseProgressData = {
        courseId,
        lastLessonId: lesson.id,
        lastLessonProgress: newProgress,
        lastUpdated: new Date().toISOString(),
        completed: newProgress >= 100
      };
      
      console.log('📊 Saving course progress:', courseProgressData);
      await setDoc(progressRef, courseProgressData, { merge: true });

      // Update lesson-specific progress
      const lessonProgressRef = doc(db, 'users', userId, 'CourseProgress', courseId, 'Lessons', lesson.id);
      const lessonProgressData = {
        lessonId: lesson.id,
        progress: newProgress,
        completed: newProgress >= 100,
        lastUpdated: new Date().toISOString(),
        timeSpent: additionalData.timeSpent || currentTime,
        quizScore: additionalData.quizScore,
        quizSubmitted: additionalData.quizSubmitted,
        quizAnswers: additionalData.quizAnswers
      };
      
      await setDoc(lessonProgressRef, lessonProgressData, { merge: true });

      setProgress(newProgress);
    } catch (error) {
      console.error('❌ Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Load progress from Firestore
  const loadProgress = async () => {
    if (!courseId || !lesson.id) return;
    
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      const { getAuth } = await import('firebase/auth');
      
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      if (!userId) return;

      const lessonProgressRef = doc(db, 'users', userId, 'CourseProgress', courseId, 'Lessons', lesson.id);
      const lessonDoc = await getDoc(lessonProgressRef);
      
      if (lessonDoc.exists()) {
        const data = lessonDoc.data();
        setProgress(data.progress || 0);
        setCurrentTime(data.timeSpent || 0);
        setQuizScore(data.quizScore || null);
        setQuizSubmitted(data.quizSubmitted || false);
        
        if (data.quizAnswers) {
          setQuizAnswers(data.quizAnswers);
        }
      }
    } catch (error) {
      console.error('❌ Error loading progress:', error);
    }
  };

  // Load progress on component mount
  useEffect(() => {
    loadProgress();
  }, [courseId, lesson.id]);

  // Prepare lesson text content for AI
  const getCombinedAIContext = (): string => {
    const contentType = lesson.content_type?.toLowerCase() || '';
    const parts: string[] = [];
    if (courseName) parts.push(`Course: ${getCleanText(courseName)}`);
    if (courseDescription) parts.push(`Course Overview: ${getCleanText(courseDescription)}`);
    parts.push(`Lesson: ${getCleanText(lesson.name)}`);
    if (useCtxText && lesson.description) parts.push(`Lesson Description: ${getCleanText(lesson.description)}`);
    if (useCtxArticle && contentType.includes('article') && lesson.article_content) {
      parts.push(`Article Content:\n${getCleanText(lesson.article_content)}`);
    }
    if (contentType.includes('assignment') && lesson.assignment_details) {
      parts.push(`Assignment Details:\n${getCleanText(lesson.assignment_details)}`);
    }
    if (useCtxQuiz && lesson.quiz && lesson.quiz.length > 0) {
      const quizSummary = lesson.quiz.map((q, i) => {
        const options = Array.isArray(q.options) ? q.options.join(' | ') : '';
        return `${i + 1}. ${getCleanText(q.question)} [${options}]`;
      }).join('\n');
      parts.push(`Quiz Questions (stems and options):\n${quizSummary}`);
    }
    if (useCtxVideo && lesson.video_url) {
      parts.push(`Video: ${lesson.video_url}`);
    }
    if (lesson.resources && lesson.resources.length > 0) {
      parts.push(`Resources: ${lesson.resources.join(', ')}`);
    }
    return parts.join('\n\n');
  };

  const runAI = async () => {
    setAiLoading(true);
    setAiError('');
    setAiResult(null);
    try {
      const ai = AIService.getInstance();
      const lessonText = getCombinedAIContext();
      if (aiMode === 'ask') {
        const response = await ai.processChatMessage('', (aiQuestion?.trim() ? aiQuestion : 'Explain this lesson in simple terms.') + `\n\nContext:\n${lessonText}`, {
          courseId,
          lessonId: lesson.id,
          lessonTitle: lesson.name,
          lessonText
        });
        setAiResult({ type: 'ask', ...response });
      } else {
        const type = aiMode; // 'summary' | 'quiz' | 'flashcards'
        const result = await ai.generateContent(courseId, lesson.id, type, lessonText);
        setAiResult(result);
      }
    } catch (e: any) {
      setAiError(e?.message || 'AI request failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Determine content type and render accordingly
  const renderContent = () => {
    const contentType = lesson.content_type?.toLowerCase() || '';
    
    // Handle video content with different sources
    if (contentType.includes('video') || lesson.video_url) {
      const videoSource = analyzeVideoUrl(lesson.video_url || '');
      
      switch (videoSource.type) {
        case 'youtube':
          return (
            <YouTubePlayer
              videoUrl={lesson.video_url || ''}
              onProgress={(newProgress) => {
                setProgress(newProgress);
                saveProgress(newProgress, {
                  timeSpent: currentTime,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
              }}
              onComplete={() => {
                setProgress(100);
                saveProgress(100, {
                  timeSpent: duration,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
                onComplete();
              }}
              onTimeUpdate={(time, duration) => {
                setCurrentTime(time);
                setDuration(duration);
              }}
            />
          );
          
        case 'google-drive':
          return (
            <GoogleDrivePlayer
              videoUrl={lesson.video_url || ''}
              onProgress={(newProgress) => {
                setProgress(newProgress);
                saveProgress(newProgress, {
                  timeSpent: currentTime,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
              }}
              onComplete={() => {
                setProgress(100);
                saveProgress(100, {
                  timeSpent: duration,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
                onComplete();
              }}
              onTimeUpdate={(time, duration) => {
                setCurrentTime(time);
                setDuration(duration);
              }}
            />
          );
          
        case 'zoom':
        case 'zoom-recording':
          return (
            <ZoomPlayer
              videoUrl={lesson.video_url || ''}
              onProgress={(newProgress) => {
                setProgress(newProgress);
                saveProgress(newProgress, {
                  timeSpent: currentTime,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
              }}
              onComplete={() => {
                setProgress(100);
                saveProgress(100, {
                  timeSpent: duration,
                  quizScore: quizScore,
                  quizSubmitted: quizSubmitted,
                  quizAnswers: quizAnswers
                });
                onComplete();
              }}
              onTimeUpdate={(time, duration) => {
                setCurrentTime(time);
                setDuration(duration);
              }}
            />
          );
          
        case 'vimeo':
          return (
            <div className="space-y-3 sm:space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <iframe
                  src={videoSource.embedUrl}
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Vimeo video player"
                />
              </div>
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Video Progress</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3">
                  External video player - progress will be tracked manually
                </p>
                <button
                  onClick={() => {
                    setProgress(100);
                    saveProgress(100, {
                      timeSpent: 0,
                      quizScore: quizScore,
                      quizSubmitted: quizSubmitted,
                      quizAnswers: quizAnswers
                    });
                    onComplete();
                  }}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Mark as Complete
                </button>
              </div>
            </div>
          );
          
        case 'direct':
          return (
            <div className="space-y-3 sm:space-y-4">
              {/* Standard HTML5 Video Player */}
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  key={`${lesson.id}-${lesson.video_url}`}
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover cursor-pointer"
                  controls
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onEnded={handleVideoEnded}
                  onClick={togglePlay}
                >
                  <source src={lesson.video_url} type="video/mp4" />
                  <source src={lesson.video_url} type="video/webm" />
                  <source src={lesson.video_url} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Video Progress Bar - Using pointer-events-none so we can click the video behind it */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 sm:p-4 pointer-events-none">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center space-x-2 sm:space-x-4 pointer-events-auto">
                      <button
                        onClick={togglePlay}
                        className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full transition-colors"
                      >
                        {isPlaying ? <PauseIcon className="h-4 w-4 sm:h-5 sm:w-5" /> : <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
                      </button>
                      <button
                        onClick={() => {
                          setIsMuted(!isMuted);
                          if (videoRef.current) {
                            videoRef.current.muted = !isMuted;
                          }
                        }}
                        className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full transition-colors pointer-events-auto"
                      >
                        {isMuted ? <SpeakerXMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" /> : <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
                      </button>
                      <span className="text-xs sm:text-sm">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Video Progress</h3>
                <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                  <div 
                    className="bg-primary-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-2">
                  {progress}% completed
                </p>
              </div>
            </div>
          );
        
        case 'hls':
          return (
            <div className="space-y-3 sm:space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover"
                  controls
                  src={lesson.video_url || ''}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onEnded={handleVideoEnded}
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <h3 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Streamed Video</h3>
                <p className="text-blue-700 text-xs sm:text-sm">
                  If playback fails on this browser, try opening the stream in a native player.
                </p>
                <a
                  href={lesson.video_url || ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center px-3 py-2 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Open Stream
                </a>
              </div>
            </div>
          );
          
        case 'unknown':
        default:
          // Fallback for unknown video types - try to embed or provide download link
          return (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-900 mb-2 text-sm sm:text-base">Video Content</h3>
                <p className="text-yellow-700 text-xs sm:text-sm mb-3">
                  This lesson contains video content that may need to be accessed externally.
                </p>
                <div className="space-y-2">
                  <a
                    href={lesson.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <VideoCameraIcon className="h-4 w-4 mr-2" />
                    Open Video
                  </a>
                  <button
                    onClick={() => {
                      setProgress(100);
                      saveProgress(100, {
                        timeSpent: 0,
                        quizScore: quizScore,
                        quizSubmitted: quizSubmitted,
                        quizAnswers: quizAnswers
                      });
                      onComplete();
                    }}
                    className="ml-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Mark as Complete
                  </button>
                </div>
              </div>
            </div>
          );
      }
    }
    
    // Handle article content
    if (contentType.includes('article') || lesson.article_content) {
      return (
        <ArticleContent
          content={lesson.article_content || lesson.description || 'No content available'}
          estimatedDuration={lesson.estimated_duration}
          onProgress={(newProgress) => {
            setProgress(newProgress);
            saveProgress(newProgress, {
              timeSpent: currentTime,
              quizScore: quizScore,
              quizSubmitted: quizSubmitted,
              quizAnswers: quizAnswers
            });
          }}
          onComplete={() => {
            setProgress(100);
            saveProgress(100, {
              timeSpent: currentTime,
              quizScore: quizScore,
              quizSubmitted: quizSubmitted,
              quizAnswers: quizAnswers
            });
            onComplete();
          }}
        />
      );
    }
    
    // Handle assignment content
    if (contentType.includes('assignment') || lesson.assignment_details) {
      return (
        <AssignmentContent
          details={lesson.assignment_details || lesson.description || 'No assignment details available'}
          estimatedDuration={lesson.estimated_duration}
          resources={lesson.resources}
          onProgress={(newProgress) => {
            setProgress(newProgress);
            saveProgress(newProgress, {
              timeSpent: currentTime,
              quizScore: quizScore,
              quizSubmitted: quizSubmitted,
              quizAnswers: quizAnswers
            });
          }}
          onComplete={() => {
            setProgress(100);
            saveProgress(100, {
              timeSpent: currentTime,
              quizScore: quizScore,
              quizSubmitted: quizSubmitted,
              quizAnswers: quizAnswers
            });
            onComplete();
          }}
        />
      );
    }
    
    // Handle quiz content
    if (contentType.includes('quiz') || (lesson.quiz && lesson.quiz.length > 0)) {
      return (
        <InteractiveQuiz
          courseId={courseId}
          lesson={lesson}
          defaultDurationSeconds={600}
          onSubmitted={({ score: s }) => {
            const newProgress = s >= 70 ? 100 : s;
            setProgress(newProgress);
            saveProgress(newProgress, {
              timeSpent: currentTime,
              quizScore: s,
              quizSubmitted: true,
              quizAnswers: quizAnswers,
            });
            if (s >= 70) onComplete();
          }}
        />
      );
    }
    
    // Default fallback for unknown content types
    return (
      <div className="prose max-w-none">
        <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Lesson Content</h3>
          <div className="text-gray-700 leading-relaxed">
            {lesson.description ? (
              <div className="mb-3 sm:mb-4">
                <p className="text-sm sm:text-base leading-relaxed">
                  {getCleanText(lesson.description)}
                </p>
              </div>
            ) : (
              <p className="mb-3 sm:mb-4 text-sm sm:text-base">
                This lesson contains content that will be displayed here.
              </p>
            )}
          </div>
          
          <button
            onClick={() => {
              saveProgress(100, {
                timeSpent: currentTime,
                quizScore: quizScore,
                quizSubmitted: quizSubmitted,
                quizAnswers: quizAnswers
              });
              onComplete();
            }}
            className="mt-4 sm:mt-6 px-4 sm:px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm sm:text-base"
          >
            Mark as Complete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Lesson Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <AcademicCapIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate max-w-lg" title={getCleanText(lesson.name)}>{getCleanText(lesson.name)}</h2>
              <p className="text-gray-600 text-sm sm:text-base truncate max-w-lg">{getCleanText(lesson.description)}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              onClick={onPrevious}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Previous Lesson"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onNext}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Next Lesson"
            >
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm text-gray-600">{progress}% completed</span>
          {isSaving && (
            <span className="text-sm text-blue-600 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Saving...
            </span>
          )}
        </div>
      </div>

      {/* AI Learning Assistant */}
      <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 border border-gray-200 rounded-lg p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
              <SparklesIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-base sm:text-lg font-bold text-gray-900">Profstudymate</div>
              <div className="text-xs sm:text-sm text-gray-600">Real-time help based on this lesson</div>
            </div>
          </div>
          <button
            onClick={() => setShowAI(!showAI)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${showAI ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}
          >
            {showAI ? 'Hide' : 'Open'}
          </button>
        </div>

        {showAI && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
              <div className="lg:col-span-3 bg-white rounded-md border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-800 flex items-center">
                    <LightBulbIcon className="h-4 w-4 text-blue-600 mr-2" /> Choose AI Task
                  </label>
                  <select
                    value={aiMode}
                    onChange={e => setAiMode(e.target.value as any)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="summary">Summary</option>
                    <option value="quiz">Generate Quiz</option>
                    <option value="flashcards">Flashcards</option>
                    <option value="ask">Ask a Question</option>
                  </select>
                </div>
                {aiMode === 'ask' && (
                  <textarea
                    value={aiQuestion}
                    onChange={e => setAiQuestion(e.target.value)}
                    placeholder="Ask anything about this lesson..."
                    className="w-full mt-2 p-2 text-sm border border-gray-300 rounded-md min-h-[80px]"
                  />
                )}
                <div className="mt-3">
                  <button
                    onClick={runAI}
                    disabled={aiLoading || (aiMode === 'ask' && !aiQuestion.trim())}
                    className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold ${aiLoading ? 'bg-gray-300 text-gray-600' : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'}`}
                  >
                    {aiLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-4 w-4 mr-2" /> Generate
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-md border border-gray-200 p-3">
                <div className="text-xs text-gray-600 mb-2">Context used</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  <label className={`inline-flex items-center px-2 py-1 rounded border text-xs ${hasText ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${useCtxText ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-300 text-gray-600'}`}>
                    <input type="checkbox" className="mr-1" disabled={!hasText} checked={useCtxText} onChange={() => setUseCtxText(!useCtxText)} /> Text
                  </label>
                  <label className={`inline-flex items-center px-2 py-1 rounded border text-xs ${hasArticle ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${useCtxArticle ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-300 text-gray-600'}`}>
                    <input type="checkbox" className="mr-1" disabled={!hasArticle} checked={useCtxArticle} onChange={() => setUseCtxArticle(!useCtxArticle)} /> Article
                  </label>
                  <label className={`inline-flex items-center px-2 py-1 rounded border text-xs ${hasVideo ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${useCtxVideo ? 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-gray-50 border-gray-300 text-gray-600'}`}>
                    <input type="checkbox" className="mr-1" disabled={!hasVideo} checked={useCtxVideo} onChange={() => setUseCtxVideo(!useCtxVideo)} /> Video
                  </label>
                  <label className={`inline-flex items-center px-2 py-1 rounded border text-xs ${hasQuiz ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${useCtxQuiz ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-gray-50 border-gray-300 text-gray-600'}`}>
                    <input type="checkbox" className="mr-1" disabled={!hasQuiz} checked={useCtxQuiz} onChange={() => setUseCtxQuiz(!useCtxQuiz)} /> Quizzes
                  </label>
                </div>
                <div className="text-xs text-gray-800 max-h-28 overflow-y-auto border rounded p-2 bg-gray-50">
                  {getCombinedAIContext().slice(0, 300) || 'No text content available'}
                </div>
              </div>
            </div>

            {aiError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{aiError}</div>
            )}

            {aiResult && (
              <div className="bg-white border border-gray-200 rounded-md p-4">
                {aiResult.type === 'summary' && aiResult.content && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                      <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                      <span>AI Summary</span>
                    </div>
                    {aiResult.content.summary && (
                      <p className="text-gray-800 text-sm leading-relaxed">{aiResult.content.summary}</p>
                    )}
                    {aiResult.content.keyPoints && Array.isArray(aiResult.content.keyPoints) && (
                      <div>
                        <div className="text-sm font-semibold mb-1">Key Points</div>
                        <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
                          {aiResult.content.keyPoints.map((p: string, idx: number) => (
                            <li key={idx}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aiResult.content.importantTerms && Array.isArray(aiResult.content.importantTerms) && (
                      <div>
                        <div className="text-sm font-semibold mb-1">Important Terms</div>
                        <div className="flex flex-wrap gap-2">
                          {aiResult.content.importantTerms.map((t: string, idx: number) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {aiResult.type === 'quiz' && aiResult.content && aiResult.content.questions && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                      <QuestionMarkCircleIcon className="h-5 w-5 text-purple-600" />
                      <span>AI-Generated Quiz</span>
                    </div>
                    <div className="space-y-3">
                      {aiResult.content.questions.map((q: any, idx: number) => (
                        <div key={idx} className="border border-gray-200 rounded-md p-3">
                          <div className="text-sm font-medium text-gray-900 mb-2">{idx + 1}. {q.question}</div>
                          <ul className="space-y-1">
                            {q.options?.map((opt: string, i: number) => (
                              <li key={i} className={`text-sm ${i === q.correctAnswer ? 'text-green-700' : 'text-gray-700'}`}>• {opt}</li>
                            ))}
                          </ul>
                          {q.explanation && (
                            <div className="mt-2 text-xs text-gray-600">Explanation: {q.explanation}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aiResult.type === 'flashcards' && aiResult.content && aiResult.content.cards && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                      <StarIcon className="h-5 w-5 text-yellow-600" />
                      <span>AI Flashcards</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {aiResult.content.cards.map((c: any, idx: number) => (
                        <div key={idx} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                          <div className="text-sm font-semibold text-gray-900">Q: {c.front}</div>
                          <div className="text-sm text-gray-700 mt-1">A: {c.back}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aiResult.type === 'ask' && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-900 font-semibold">
                      <LightBulbIcon className="h-5 w-5 text-green-600" />
                      <span>AI Answer</span>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{aiResult.response}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lesson Content */}
      {renderContent()}
    </div>
  );
};

export default LessonContent;
